/**
 * Sniffs a file's *content* (magic bytes) rather than trusting its
 * declared MIME type or extension. A renamed text file (e.g. `notes.txt`
 * saved as `fake.png`) reports `image/png` for `File.type` in some
 * browsers/OSes and always keeps a `.png` name, so the extension/MIME
 * checks in ProductFormPage were not enough to catch it before preview -
 * see the QA report, P2 #10 ("image picker accepts invalid files until
 * Save").
 */
export type SniffedImageType = 'jpeg' | 'png' | 'webp';

const JPEG_MAGIC = [0xff, 0xd8, 0xff];
const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47];

function bytesMatch(bytes: Uint8Array, offset: number, magic: number[]): boolean {
  if (bytes.length < offset + magic.length) return false;
  for (let i = 0; i < magic.length; i++) {
    if (bytes[offset + i] !== magic[i]) return false;
  }
  return true;
}

/**
 * Returns the sniffed image type for the given bytes, or `null` if the
 * bytes don't match any supported image's magic-byte signature.
 *
 * - JPEG: `FF D8 FF`
 * - PNG: `89 50 4E 47`
 * - WebP: `RIFF` (bytes 0-3) then `WEBP` (bytes 8-11), per the RIFF
 *   container format - the 4 bytes in between are a file-size field, not
 *   part of the signature.
 */
export function sniffImageType(bytes: Uint8Array): SniffedImageType | null {
  if (bytesMatch(bytes, 0, JPEG_MAGIC)) return 'jpeg';
  if (bytesMatch(bytes, 0, PNG_MAGIC)) return 'png';
  if (bytes.length >= 12) {
    const isRiff =
      bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46; // "RIFF"
    const isWebp =
      bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50; // "WEBP"
    if (isRiff && isWebp) return 'webp';
  }
  return null;
}

/**
 * Reads just enough of the front of a `File`/`Blob` to sniff its type.
 * Uses `FileReader` (not `Blob#arrayBuffer()`) since that's implemented
 * consistently everywhere this needs to run, including jsdom in tests -
 * `Blob#arrayBuffer()`/`.slice().arrayBuffer()` are not.
 */
export async function sniffImageFile(file: Blob): Promise<SniffedImageType | null> {
  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsArrayBuffer(file.slice(0, 12));
  });
  return sniffImageType(new Uint8Array(buffer));
}
